import Phaser from 'phaser';
import { computeWallTiles, WALL_ATLAS, type WallCell } from '@campus/core';
import { extrudeTileTexture } from './extrudeTiles.js';

export const WALL_ATLAS_KEY = 'walls-atlas';

/** À appeler dans preload() de toute scène qui affiche des murs d'éditeur. */
export function preloadWallAtlas(scene: Phaser.Scene): void {
  if (scene.textures.exists(WALL_ATLAS_KEY)) return;
  scene.load.spritesheet(WALL_ATLAS_KEY, WALL_ATLAS.url, {
    frameWidth: WALL_ATLAS.tileSize,
    frameHeight: WALL_ATLAS.tileSize,
  });
}

/**
 * Rendu des murs `map_walls` (partagé ZoneScene / EditorScene).
 *
 * L'apparence d'une cellule dépend de ses voisines (computeWallTiles) : un
 * ajout/retrait change aussi les cellules autour — on redessine donc tout à
 * chaque changement (quelques centaines de sprites au plus, négligeable).
 * La collision reste gérée par les scènes, une cellule peinte = un bloc.
 */
export class WallRenderer {
  private sprites: Phaser.GameObjects.Image[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    /** Depth d'une tuile à partir du bas (en px) de la cellule peinte qui la possède. */
    private readonly depthForBottomY: (bottomY: number) => number,
  ) {}

  render(cells: WallCell[]): void {
    this.clear();
    const ts = WALL_ATLAS.tileSize;
    // Atlas extrudé : pas de traits entre les tuiles aux zooms non entiers.
    const atlasKey = extrudeTileTexture(this.scene, WALL_ATLAS_KEY, ts, ts);
    for (const tile of computeWallTiles(cells)) {
      const sprite = this.scene.add.image(tile.tileX * ts, tile.tileY * ts, atlasKey, tile.frame);
      sprite.setOrigin(0, 0);
      sprite.setDepth(this.depthForBottomY((tile.ownerTileY + 1) * ts));
      this.sprites.push(sprite);
    }
  }

  clear(): void {
    for (const s of this.sprites) s.destroy();
    this.sprites = [];
  }
}
